import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/_atoms/button";
import { buildDatatableToolbarRowClass } from "@/domain/view/components/datatable/datatable-toolbar-row-class";
import { Cross2Icon } from "@radix-ui/react-icons";

import { StoredViewUnsavedButtons } from "../stored-view-menu/stored-view-unsaved-buttons";
import { useHasUnsavedStoredView } from "../stored-view-menu/use-has-unsaved-stored-view";

import { ConfigurationViewConfig } from "./datatable-configuration-view/datatable-configuration-view";
import { defaultValueIfUndefined } from "./datatable-utils/config-helpers";
import { useDatatableContext } from "./use-datatable-context";

interface DatatableAppliedFilterViewProps {
  config?: ConfigurationViewConfig;
}

export const DatatableAppliedFilterView = ({ config }: DatatableAppliedFilterViewProps) => {
  const { t } = useTranslation("common");
  const { table, state, lock, getConfigurationForColumn } = useDatatableContext();
  const { columnFilters } = state;
  const { hasUnsavedChanges } = useHasUnsavedStoredView();

  const filterChipColumnList = useMemo(() => {
    return table
      .getAllColumns()
      .filter(
        (column) => column.getCanFilter() && lock.canShowFilterChip(column.id) && Boolean(column.getFilterValue()),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table, columnFilters, lock]);

  const shouldRenderFilterButton = defaultValueIfUndefined<boolean>({
    val: config?.filterButton,
    defaultVal: true,
  });

  if (!shouldRenderFilterButton) {
    return null;
  }

  const hasActiveFilters = filterChipColumnList.length > 0;
  const hasRightContent = !!config?.appliedFilterRightContent || (!!config?.storedViewMenu && hasUnsavedChanges);

  if (!hasActiveFilters && !hasRightContent) {
    return null;
  }

  return (
    <div
      className={buildDatatableToolbarRowClass(!!config?.noInset)}
      data-testid="datatable-applied-filter-view"
    >
      <div className="flex flex-row items-center justify-start gap-1">
        {filterChipColumnList.map((column) => {
          const filterPaneView = getConfigurationForColumn(column.id)?.renderAppliedFilterView();

          if (!filterPaneView) {
            return <></>;
          }

          return <div key={column.id}>{filterPaneView}</div>;
        })}
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="xs"
            className="h-7 gap-1.5 px-1.5 text-xs font-normal text-foreground/60 hover:text-foreground"
            onClick={() => table.setColumnFilters(lock.publishedColumnFilterList)}
            aria-label={t("view.removeFilter")}
          >
            {t("view.removeFilter")}
            <Cross2Icon className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
      <div className="flex grow flex-row items-center justify-start"></div>
      <div className="flex flex-row items-center justify-end gap-2">
        {config?.appliedFilterRightContent}
        {config?.storedViewMenu && <StoredViewUnsavedButtons />}
      </div>
    </div>
  );
};
